import React, { useState, useEffect, useCallback, useMemo, startTransition } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { flushSync } from 'react-dom';
import { GET_MAIN_CATEGORIES, GET_PRODUCTS } from '../../apollo/queries';
import { useLoading } from '../../contexts/LoadingContext';
import ProductModal from '../../components/ProductModal';
import '../css/ShopPage.css';

const ProductCard = ({ product, onCartClick, onImageClick }) => {
    // 이미지 URL을 안전하게 파싱하는 함수
    const getImageUrl = (images) => {
        if (!images) {
            return null; // null을 반환하면 SVG 표시
        }
        
        if (Array.isArray(images)) {
            return images[0] || null;
        }
        
        if (typeof images === 'string') {
            // 빈 문자열이나 "[]" 체크
            if (images.trim() === '' || images.trim() === '[]') {
                return null;
            }
            
            const trimmedImages = images.trim();
            
            // 먼저 URL 패턴을 찾아서 추출 (JSON 파싱 전에 시도)
            const urlPattern = /https?:\/\/[^\s,\]"'}]+/g;
            const urlMatches = trimmedImages.match(urlPattern);
            
            if (urlMatches && urlMatches.length > 0) {
                // 첫 번째 완전한 URL을 반환
                return urlMatches[0];
            }
            
            // 단순한 URL 문자열인 경우
            if (!trimmedImages.startsWith('[') && !trimmedImages.startsWith('"')) {
                // 상대 경로나 단순 URL인 경우
                if (trimmedImages.includes('/') || trimmedImages.includes('http')) {
                    return trimmedImages;
                }
                return trimmedImages;
            }
            
            // JSON 파싱 시도 (완전한 JSON인 경우만)
            try {
                // JSON 문자열이 완전한지 확인
                if (trimmedImages.startsWith('[') && trimmedImages.endsWith(']')) {
                    const parsedImages = JSON.parse(trimmedImages);
                    if (Array.isArray(parsedImages) && parsedImages.length > 0 && parsedImages[0]) {
                        return parsedImages[0];
                    }
                } else if (trimmedImages.startsWith('"') && trimmedImages.endsWith('"')) {
                    const parsedImages = JSON.parse(trimmedImages);
                    if (typeof parsedImages === 'string' && parsedImages.trim() !== '') {
                        return parsedImages;
                    }
                }
            } catch (error) {
                // JSON 파싱 실패 시 더 이상 에러를 로그하지 않음
                // 이미 위에서 URL을 찾았으므로 여기서 실패해도 괜찮음
            }
            
            // 위의 모든 방법이 실패한 경우, 문자열에서 URL 부분만 추출
            const partialUrlMatch = trimmedImages.match(/https?:\/\/[^"'\s}]+/);
            if (partialUrlMatch) {
                return partialUrlMatch[0];
            }
            
            // 상대 경로 추출
            const pathMatch = trimmedImages.match(/\/[^"'\s}]+/);
            if (pathMatch) {
                return pathMatch[0];
            }
        }
        
        return null;
    };

    const imageUrl = getImageUrl(product.images);

    // SVG 플레이스홀더 컴포넌트
    const ImagePlaceholder = () => (
        <div className="image-placeholder">
            <svg width="50" height="50" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="#cccccc" strokeWidth="1.5">
                <path d="M21.25 4.75H2.75C2.58424 4.75 2.42527 4.81585 2.30806 4.93306C2.19085 5.05027 2.125 5.20924 2.125 5.375V18.625C2.125 18.7908 2.19085 18.9497 2.30806 19.0669C2.42527 19.1842 2.58424 19.25 2.75 19.25H21.25C21.4158 19.25 21.5747 19.1842 21.6919 19.0669C21.8092 18.9497 21.875 18.7908 21.875 18.625V5.375C21.875 5.20924 21.8092 5.05027 21.6919 4.93306C21.5747 4.81585 21.4158 4.75 21.25 4.75V4.75Z" strokeLinecap="round" strokeLinejoin="round"></path>
                <path d="M8.75 11.25C9.7165 11.25 10.5 10.4665 10.5 9.5C10.5 8.5335 9.7165 7.75 8.75 7.75C7.7835 7.75 7 8.5335 7 9.5C7 10.4665 7.7835 11.25 8.75 11.25Z" strokeLinecap="round" strokeLinejoin="round"></path>
                <path d="M21.875 15.25L16.25 9.625L7 18.875" strokeLinecap="round" strokeLinejoin="round"></path>
            </svg>
        </div>
    );

    return (
        <div className="shoppage-product-card">
            <div className="shoppage-product-image" onClick={() => onImageClick(product)}>
                {imageUrl ? (
                    <img 
                        src={imageUrl}
                        alt={product.name}
                        onError={(e) => {
                            // 이미지 로딩 실패 시 부모 요소를 SVG로 교체
                            e.target.style.display = 'none';
                            const placeholder = e.target.parentNode.querySelector('.fallback-placeholder');
                            if (placeholder) {
                                placeholder.style.display = 'flex';
                            }
                        }}
                    />
                ) : null}
                
                {/* SVG 플레이스홀더 - 이미지가 없을 때 표시 */}
                {!imageUrl && <ImagePlaceholder />}
                
                {/* 이미지 로딩 실패 시 fallback */}
                {imageUrl && (
                    <div className="fallback-placeholder" style={{ display: 'none' }}>
                        <ImagePlaceholder />
                    </div>
                )}
            </div>
            <div className="shoppage-product-info">
                <h4>{product.name}</h4>
                <div className="shoppage-product-details">
                    <p><strong>상품 코드:</strong> {product.productCode || '미입력'}</p>
                    <p><strong>사이즈:</strong> {product.size || '미입력'}</p>
                    {product.options && product.options.length > 0 && (
                        <p><strong>옵션:</strong> {product.options.join(', ')}</p>
                    )}
                </div>
                {product.description && <p className="shoppage-product-description">{product.description}</p>}
            </div>
            <div className="shoppage-product-actions">
                <button className="add-to-cart-icon-btn" onClick={() => onCartClick(product)}>
                    <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#000000"><path d="M0 0h24v24H0V0z" fill="none"/><path d="M11 9h2V6h3V4h-3V1h-2v3H8v2h3v3zm-4 9c-1.1 0-1.99.9-1.99 2S5.9 22 7 22s2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-1.99.9-1.99 2s.89 2 1.99 2 2-.9 2-2-.9-2-2-2zm-8.9-5h7.45c.75 0 1.41-.41 1.75-1.03l3.86-7.01L19.42 4l-3.87 7H8.53L4.27 2H1v2h2l3.6 7.59-1.35 2.44C4.52 15.37 5.48 17 7 17h12v-2H7l1.1-2z"/></svg>
                </button>
            </div>
        </div>
    );
};

const ShopPage = () => {
    const [modalProduct, setModalProduct] = useState(null);
    const { startLoading, stopLoading, updateMessage } = useLoading();
    
    // 상태를 하나의 객체로 통합하여 관리
    const [categoryState, setCategoryState] = useState({
        selectedCategoryId: null,
        selectedCategoryType: null,
        selectedMainCategoryId: null
    });
    
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // GraphQL 쿼리 실행
    const { data: categoriesData, loading: categoriesLoading, error: categoriesError } = useQuery(GET_MAIN_CATEGORIES);
    
    // URL 파라미터 읽기
    const categoryParam = searchParams.get('category');
    const typeParam = searchParams.get('type');
    const mainCategoryParam = searchParams.get('mainCategory');
    
    // 페이지네이션 파라미터 읽기
    const pageParam = parseInt(searchParams.get('page') || '1', 10);
    const itemsPerPageParam = parseInt(searchParams.get('limit') || '18', 10);
    
    // 페이지네이션 상태
    const [currentPage, setCurrentPage] = useState(pageParam);
    const [itemsPerPage, setItemsPerPage] = useState(itemsPerPageParam);
    
    // 제품 쿼리 변수 설정 (useMemo로 최적화)
    const productQueryVariables = useMemo(() => {
        const variables = {
            isActive: true,
            limit: itemsPerPage,
            offset: (currentPage - 1) * itemsPerPage
        };
        
        if (categoryParam && typeParam === 'sub') {
            // 하위 카테고리 선택 시
            variables.subCategoryId = categoryParam;
            variables.mainCategoryId = mainCategoryParam;
        } else if (categoryParam && typeParam !== 'sub') {
            // 메인 카테고리 선택 시
            variables.mainCategoryId = categoryParam;
        }
        
        return variables;
    }, [categoryParam, typeParam, mainCategoryParam, currentPage, itemsPerPage]);
    
    // 전체 제품 개수 조회 (limit 1로 totalCount만 가져옴)
    const { data: allProductsData, loading: allProductsLoading, error: allProductsError } = useQuery(GET_PRODUCTS, {
        variables: {
            isActive: true,
            limit: 1,
            offset: 0
        }
    });
    
    // 필터링된 제품 쿼리 (실제 표시용)
    const { data: productsData, loading: productsLoading, error: productsError } = useQuery(GET_PRODUCTS, {
        variables: productQueryVariables
    });

    // URL 파라미터에서 카테고리 정보 읽기 - useCallback으로 최적화
    const updateCategoryFromParams = useCallback(() => {
        if (categoryParam) {
            const newState = {
                selectedCategoryId: categoryParam,
                selectedCategoryType: typeParam || 'main',
                selectedMainCategoryId: null
            };
            
            if (typeParam === 'main') {
                newState.selectedMainCategoryId = categoryParam;
            } else if (typeParam === 'sub' && mainCategoryParam) {
                newState.selectedMainCategoryId = mainCategoryParam;
            }
            
            setCategoryState(newState);
        } else {
            setCategoryState({
                selectedCategoryId: null,
                selectedCategoryType: null,
                selectedMainCategoryId: null
            });
        }
    }, [categoryParam, typeParam, mainCategoryParam]);

    useEffect(() => {
        updateCategoryFromParams();
    }, [updateCategoryFromParams]);
    
    // URL 파라미터에서 페이지네이션 정보 읽기
    useEffect(() => {
        const page = parseInt(searchParams.get('page') || '1', 10);
        const limit = parseInt(searchParams.get('limit') || '18', 10);
        setCurrentPage(page);
        setItemsPerPage(limit);
    }, [searchParams]);
    
    // 카테고리 변경 시 첫 페이지로 리셋
    useEffect(() => {
        if (categoryParam) {
            setCurrentPage(1);
            const params = new URLSearchParams(searchParams);
            params.set('page', '1');
            setSearchParams(params, { replace: true });
        }
    }, [categoryParam, typeParam, mainCategoryParam]);

    // 로딩 상태 관리
    useEffect(() => {
        if (categoriesLoading || allProductsLoading || productsLoading) {
            if (categoriesLoading) {
                startLoading('카테고리를 불러오는 중...');
            } else if (allProductsLoading || productsLoading) {
                updateMessage('제품을 검색하는 중...');
            }
        } else {
            stopLoading();
        }
    }, [categoriesLoading, allProductsLoading, productsLoading, startLoading, stopLoading, updateMessage]);

    const handleProductClick = useCallback((product) => {
        setModalProduct(product);
    }, []);

    const handleCloseModal = useCallback(() => {
        setModalProduct(null);
    }, []);
    
    const handleImageClick = useCallback((product) => {
        navigate(`/product?id=${product.id}`);
    }, [navigate]);

    // 카테고리 클릭 핸들러를 useCallback으로 최적화하고 상태 업데이트 안정화
    const handleCategoryClick = useCallback((categoryId, categoryType = null) => {
        // GraphQL 데이터 직접 사용
        const mainCategories = categoriesData?.mainCategories || [];
        
        // startTransition으로 상태 업데이트를 지연 처리하여 DOM 안정성 확보
        startTransition(() => {
            if (categoryId) {
                const params = { category: categoryId, page: '1' };
                
                // 새로운 상태 객체 생성
                const newState = {
                    selectedCategoryId: categoryId,
                    selectedCategoryType: categoryType || 'main',
                    selectedMainCategoryId: null
                };
                
                if (categoryType) {
                    params.type = categoryType;
                    
                    if (categoryType === 'main') {
                        newState.selectedMainCategoryId = categoryId;
                    } else if (categoryType === 'sub') {
                        // mainCategories에서 해당 subCategory가 속한 mainCategory를 찾기
                        for (const mainCat of mainCategories) {
                            if (mainCat.subCategories && mainCat.subCategories.some(sub => sub.id === categoryId)) {
                                params.mainCategory = mainCat.id;
                                newState.selectedMainCategoryId = mainCat.id;
                                break;
                            }
                        }
                    }
                } else {
                    newState.selectedMainCategoryId = categoryId;
                }
                
                // URL 업데이트는 즉시, 상태 업데이트는 transition으로 처리
                setSearchParams(params);
                setCategoryState(newState);
                setCurrentPage(1);
            } else {
                // 전체보기 선택시 상태와 파라미터 모두 초기화
                setSearchParams({ page: '1' });
                setCategoryState({
                    selectedCategoryId: null,
                    selectedCategoryType: null,
                    selectedMainCategoryId: null
                });
                setCurrentPage(1);
            }
        });
    }, [categoriesData?.mainCategories, setSearchParams]);
    
    // 페이지네이션 핸들러
    const handlePageChange = useCallback((newPage) => {
        setCurrentPage(newPage);
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [searchParams, setSearchParams]);
    
    // 아이템 개수 변경 핸들러
    const handleItemsPerPageChange = useCallback((newItemsPerPage) => {
        setItemsPerPage(newItemsPerPage);
        setCurrentPage(1);
        const params = new URLSearchParams(searchParams);
        params.set('limit', newItemsPerPage.toString());
        params.set('page', '1');
        setSearchParams(params, { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [searchParams, setSearchParams]);

    // 로딩 상태
    if (categoriesLoading || allProductsLoading || productsLoading) {
        return (
            <div className="shop-page">
                <div className="shop-breadcrumb">
                    <div className="breadcrumb-content">
                        <span>홈</span>
                        <span>{'>'}</span>
                        <span className="current">모선 부스바</span>
                    </div>
                </div>
                <main className="shop-main-content">
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                        <div>상품 정보를 불러오는 중입니다...</div>
                    </div>
                </main>
            </div>
        );
    }

    // 에러 상태
    if (categoriesError || allProductsError || productsError) {
        return (
            <div className="shop-page">
                <div className="shop-breadcrumb">
                    <div className="breadcrumb-content">
                        <span>홈</span>
                        <span>{'>'}</span>
                        <span className="current">모선 부스바</span>
                    </div>
                </div>
                <main className="shop-main-content">
                    <div style={{ textAlign: 'center', padding: '50px', color: '#ff4444' }}>
                        <div>데이터를 불러오는 중 오류가 발생했습니다.</div>
                    </div>
                </main>
            </div>
        );
    }

    const mainCategories = categoriesData?.mainCategories || [];
    const totalProductCount = allProductsData?.products?.totalCount || 0; // 전체 상품 개수
    const products = productsData?.products?.products || []; // 실제 표시용 제품 목록
    const totalProducts = productsData?.products?.totalCount || 0; // 현재 필터링된 상품 개수

    // 활성화된 메인 카테고리만 필터링
    const activeMainCategories = mainCategories.filter(cat => cat.isActive);
    
    // 페이지네이션 계산
    const totalPages = Math.ceil(totalProducts / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, totalProducts);
    
    // 페이지 번호 생성 (최대 10개 표시)
    const getPageNumbers = () => {
        const pages = [];
        const maxPagesToShow = 10;
        let startPage = Math.max(1, currentPage - Math.floor(maxPagesToShow / 2));
        let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);
        
        if (endPage - startPage < maxPagesToShow - 1) {
            startPage = Math.max(1, endPage - maxPagesToShow + 1);
        }
        
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };
    
    let currentCategory = null;
    
    const { selectedCategoryId, selectedCategoryType, selectedMainCategoryId } = categoryState;
    
    if (selectedCategoryId && selectedCategoryType) {
        if (selectedCategoryType === 'main') {
            const selectedMainCategory = mainCategories.find(cat => cat.id === selectedCategoryId);
            if (selectedMainCategory) {
                currentCategory = selectedMainCategory;
            }
        } else if (selectedCategoryType === 'sub') {
            // 하위 카테고리에서 찾기
            const selectedSubCategory = mainCategories
                .flatMap(main => main.subCategories || [])
                .find(sub => sub.id === selectedCategoryId);
            if (selectedSubCategory) {
                currentCategory = selectedSubCategory;
            }
        }
    }

    // 카테고리별 상품 개수 계산 함수들 (카테고리 데이터의 productCount 사용)
    const getMainCategoryProductCount = (mainCategoryId) => {
        const category = mainCategories.find(cat => cat.id === mainCategoryId);
        return category?.productCount || 0;
    };

    const getSubCategoryProductCount = (subCategoryId) => {
        for (const mainCat of mainCategories) {
            const subCat = mainCat.subCategories?.find(sub => sub.id === subCategoryId);
            if (subCat) {
                return subCat.productCount || 0;
            }
        }
        return 0;
    };

    // 현재 선택된 메인 카테고리를 찾기
    const getCurrentMainCategory = () => {
        // selectedMainCategoryId state를 우선적으로 사용
        if (selectedMainCategoryId) {
            const mainCat = mainCategories.find(cat => cat.id === selectedMainCategoryId);
            if (mainCat) return mainCat;
        }
        
        if (!selectedCategoryId) return null;
        
        if (selectedCategoryType === 'main') {
            return mainCategories.find(cat => cat.id === selectedCategoryId);
        } else if (selectedCategoryType === 'sub') {
            // fallback: 하위 카테고리가 선택된 경우, 해당 하위 카테고리가 속한 메인 카테고리를 찾음
            for (const mainCat of mainCategories) {
                if (mainCat.subCategories && mainCat.subCategories.some(sub => sub.id === selectedCategoryId)) {
                    return mainCat;
                }
            }
        }
        return null;
    };

    const currentMainCategory = getCurrentMainCategory();

    return (
        <div className="shop-page">
            {modalProduct && <ProductModal key="shop-product-modal" product={modalProduct} onClose={handleCloseModal} />}
                            <div className="shop-breadcrumb">
                    <div className="breadcrumb-content">
                        <span>홈</span>
                        <span>{'>'}</span>
                        <span className="current">
                            {currentCategory ? currentCategory.name : '전체 상품'}
                        </span>
                    </div>
                </div>
            <main className="shop-main-content">
                <aside className="category-sidebar" key={`sidebar-${selectedCategoryId || 'none'}-${selectedCategoryType || 'none'}`}>
                    <h3>카테고리</h3>
                    <ul key={`category-list-${selectedCategoryId || 'none'}-${selectedCategoryType || 'none'}`}>
                        {/* 메인카테고리나 하위카테고리가 선택된 경우 */}
                        {currentMainCategory ? (
                            <React.Fragment key={`selected-main-${currentMainCategory.id}-${selectedCategoryType}`}>
                                <li key={`main-view-${currentMainCategory.id}`} className={selectedCategoryType === 'main' ? 'active' : ''}>
                                    <button 
                                        type="button"
                                        className="category-button"
                                        onClick={() => handleCategoryClick(currentMainCategory.id, 'main')}
                                    >
                                        {selectedCategoryType === 'main' && <span className="active-dot">●</span>}
                                        전체보기
                                        <span className="count">{getMainCategoryProductCount(currentMainCategory.id)}</span>
                                    </button>
                                </li>
                                {/* 선택된 메인카테고리의 서브카테고리들만 표시 */}
                                {(currentMainCategory.subCategories || []).filter(sub => sub.isActive).map((subCategory, index) => (
                                    <li key={`selected-sub-${subCategory.id}-${index}`} className={selectedCategoryId === subCategory.id && selectedCategoryType === 'sub' ? 'active' : ''}>
                                        <button 
                                            type="button"
                                            className="category-button subcategory-button"
                                            onClick={() => handleCategoryClick(subCategory.id, 'sub')}
                                        >
                                            {selectedCategoryId === subCategory.id && selectedCategoryType === 'sub' && <span className="active-dot">●</span>}
                                            {subCategory.name}
                                            <span className="count">{getSubCategoryProductCount(subCategory.id)}</span>
                                        </button>
                                    </li>
                                ))}
                            </React.Fragment>
                        ) : (
                            /* 아무 카테고리도 선택되지 않은 경우 전체 카테고리 표시 */
                            <React.Fragment key={`all-categories-${selectedCategoryId || 'none'}`}>
                                <li key="all-view" className={selectedCategoryId === null ? 'active' : ''}>
                                    <button 
                                        type="button"
                                        className="category-button"
                                        onClick={() => handleCategoryClick(null)}
                                    >
                                        {selectedCategoryId === null && <span className="active-dot">●</span>}
                                        전체보기
                                        <span className="count">{totalProductCount}</span>
                                    </button>
                                </li>
                                {activeMainCategories.map((category, categoryIndex) => {
                                    const activeSubCategories = (category.subCategories || []).filter(sub => sub.isActive);
                                    const hasSubCategories = activeSubCategories.length > 0;
                                    
                                    return (
                                        <li key={`all-main-${category.id}-${categoryIndex}`} className={selectedCategoryId === category.id && selectedCategoryType === 'main' ? 'active' : ''}>
                                            <button 
                                                type="button"
                                                className="category-button"
                                                onClick={() => handleCategoryClick(category.id, 'main')}
                                            >
                                                {selectedCategoryId === category.id && selectedCategoryType === 'main' && <span className="active-dot">●</span>}
                                                {category.name}
                                                <span className="count">{getMainCategoryProductCount(category.id)}</span>
                                            </button>
                                            {/* 하위 카테고리가 있는 경우 표시 */}
                                            {hasSubCategories && (
                                                <ul className="subcategory-list" key={`all-sub-list-${category.id}-${categoryIndex}`}>
                                                    {activeSubCategories.map((subCategory, subIndex) => (
                                                        <li key={`all-sub-${subCategory.id}-${subIndex}`} className={selectedCategoryId === subCategory.id && selectedCategoryType === 'sub' ? 'active' : ''}>
                                                            <button 
                                                                type="button"
                                                                className="category-button subcategory-button"
                                                                onClick={() => handleCategoryClick(subCategory.id, 'sub')}
                                                            >
                                                                {selectedCategoryId === subCategory.id && selectedCategoryType === 'sub' && <span className="active-dot">●</span>}
                                                                {subCategory.name}
                                                                <span className="count">{getSubCategoryProductCount(subCategory.id)}</span>
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </li>
                                    );
                                })}
                            </React.Fragment>
                        )}
                    </ul>
                </aside>
                <section className="product-listing" key={`products-${selectedCategoryId || 'all'}-${selectedCategoryType || 'none'}`}>
                    <div className="listing-header">
                        <h2>{currentCategory ? currentCategory.name : '전체 상품'}</h2>
                        <div className="listing-header-right">
                            <p className="product-count">총 {totalProducts}개의 상품</p>
                            <div className="items-per-page-selector">
                                <select 
                                    id="items-per-page"
                                    value={itemsPerPage} 
                                    onChange={(e) => handleItemsPerPageChange(parseInt(e.target.value, 10))}
                                    className="items-per-page-select"
                                >
                                    <option value="18">18개</option>
                                    <option value="36">36개</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    {products.length > 0 || totalProducts > 0 ? (
                        <>
                            <div className="shoppage-product-grid" key={`grid-${selectedCategoryId || 'all'}-${selectedCategoryType || 'none'}`}>
                                {products.map((product, index) => (
                                    <ProductCard 
                                        key={`product-${product.id}-${selectedCategoryId || 'all'}-${index}`} 
                                        product={product} 
                                        onCartClick={handleProductClick} 
                                        onImageClick={handleImageClick} 
                                    />
                                ))}
                            </div>
                            {totalPages > 1 && (
                                <div className="pagination-container">
                                    <div className="pagination">
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            aria-label="이전 페이지"
                                        >
                                            ‹
                                        </button>
                                        <div className="pagination-numbers">
                                            {currentPage > 5 && (
                                                <>
                                                    <button 
                                                        className="pagination-number"
                                                        onClick={() => handlePageChange(1)}
                                                    >
                                                        1
                                                    </button>
                                                    {currentPage > 6 && <span className="pagination-ellipsis">...</span>}
                                                </>
                                            )}
                                            {getPageNumbers().map((pageNum) => (
                                                <button
                                                    key={pageNum}
                                                    className={`pagination-number ${pageNum === currentPage ? 'active' : ''}`}
                                                    onClick={() => handlePageChange(pageNum)}
                                                >
                                                    {pageNum}
                                                </button>
                                            ))}
                                            {currentPage < totalPages - 4 && (
                                                <>
                                                    {currentPage < totalPages - 5 && <span className="pagination-ellipsis">...</span>}
                                                    <button 
                                                        className="pagination-number"
                                                        onClick={() => handlePageChange(totalPages)}
                                                    >
                                                        {totalPages}
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                        <button 
                                            className="pagination-btn"
                                            onClick={() => handlePageChange(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            aria-label="다음 페이지"
                                        >
                                            ›
                                        </button>
                                    </div>
                                    <div className="pagination-info">
                                        {startIndex + 1}-{Math.min(endIndex, totalProducts)} / {totalProducts}
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '50px' }} key={`empty-${selectedCategoryId || 'all'}`}>
                            <p>해당 카테고리에 상품이 없습니다.</p>
                        </div>
                    )}
                </section>
            </main>
        </div>
    );
};

export default ShopPage; 